import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../utils/api';
import { useLocation } from '../contexts/LocationContext';
import { useMasterData } from '../hooks/useApi';

interface Pond {
  id: string;
  name: string;
  pond_type: string;
  size_sqm?: number;
  capacity_kg?: number;
}

interface CreateCycleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateCycleForm: React.FC<CreateCycleFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const { selectedLocation } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ponds, setPonds] = useState<Pond[]>([]);
  
  // Get fish species from master data
  const { data: speciesData } = useMasterData('fish_species', selectedLocation?.id || null);
  
  const [formData, setFormData] = useState({
    pond_id: '',
    cycle_name: '',
    planned_start_date: '',
    fish_species: ['tilapia'] as string[], // Multi-species support
    initial_seed_size_g: 100,
    target_harvest_size_g: 1000,
    initial_stocking_count: 5000,
    cycle_number: 1,
    season_year: new Date().getFullYear()
  });

  // State for existing species inventory
  const [existingSpeciesInventory, setExistingSpeciesInventory] = useState<{[key: string]: any}>({});
  const [isLoadingSpeciesInventory, setIsLoadingSpeciesInventory] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);

  // Currency formatting function
  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) { // 1 crore or more
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) { // 1 lakh or more
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) { // 1 thousand or more
      return `₹${(amount / 1000).toFixed(1)}K`;
    } else {
      return `₹${amount.toFixed(0)}`;
    }
  };

  // Get market price based on species from master data
  const getMarketPrice = (species: string) => {
    if (speciesData) {
      const speciesInfo = speciesData.find(s => s.name.toLowerCase() === species.toLowerCase());
      return speciesInfo?.market_price_per_kg || 100; // Default to ₹100/kg if not found
    }
    // Fallback pricing if master data not loaded
    const speciesLower = species.toLowerCase();
    if (speciesLower.includes('rohu') || speciesLower.includes('catla') || speciesLower.includes('mrigal')) {
      return 120; // IMC varieties
    } else if (speciesLower.includes('grass') || speciesLower.includes('silver') || speciesLower.includes('bighead')) {
      return 130; // EMC varieties
    } else {
      return 100; // Default for others
    }
  };

  // Calculate projected revenue for multi-species with realistic growth distribution
  const calculateProjectedRevenue = () => {
    let totalRevenue = 0;
    const speciesBreakdown: {[key: string]: number} = {};
    
    formData.fish_species.forEach(species => {
      const marketPrice = getMarketPrice(species);
      
      // Apply realistic growth distribution (70% reach target, 30% partial)
      const successfulFishCount = Math.floor(formData.initial_stocking_count * 0.7);
      const partialFishCount = formData.initial_stocking_count - successfulFishCount;
      const partialWeightG = Math.min(formData.target_harvest_size_g * 0.75, 750.0);
      
      const successfulBiomassKg = (formData.target_harvest_size_g * successfulFishCount) / 1000;
      const partialBiomassKg = (partialWeightG * partialFishCount) / 1000;
      const totalBiomassKg = successfulBiomassKg + partialBiomassKg;
      
      const speciesRevenue = totalBiomassKg * marketPrice;
      speciesBreakdown[species] = speciesRevenue;
      totalRevenue += speciesRevenue;
    });
    
    return { totalRevenue, speciesBreakdown };
  };

  // Recalculate projected revenue when form data changes
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [speciesRevenueBreakdown, setSpeciesRevenueBreakdown] = useState<{[key: string]: number}>({});
  
  useEffect(() => {
    const result = calculateProjectedRevenue();
    setProjectedRevenue(result.totalRevenue);
    setSpeciesRevenueBreakdown(result.speciesBreakdown);
  }, [formData.initial_stocking_count, formData.target_harvest_size_g, formData.fish_species]);

  // Fetch species inventory when pond changes
  useEffect(() => {
    if (formData.pond_id) {
      // Reset species inventory to avoid stale data
      setExistingSpeciesInventory({});
      setIsLoadingSpeciesInventory(true);
      fetchExistingSpeciesInventory(formData.pond_id);
    } else {
      // Clear species inventory when no pond selected
      setExistingSpeciesInventory({});
      setIsLoadingSpeciesInventory(false);
    }
  }, [formData.pond_id]);

  // Reset species selection when pond changes to avoid stale data
  useEffect(() => {
    if (formData.pond_id) {
      // Reset to default species selection when pond changes
      setFormData(prev => ({
        ...prev,
        fish_species: ['tilapia'] // Reset to default single species
      }));
    }
  }, [formData.pond_id]);

  // Fetch existing species inventory for selected pond
  const fetchExistingSpeciesInventory = async (pondId: string) => {
    if (!pondId) return;
    
    try {
      const response = await apiClient.get(`/ponds/${pondId}/species-inventory`);
      const inventory: {[key: string]: any} = {};
      response.data.forEach((item: any) => {
        const key = (item.species || '').toLowerCase();
        inventory[key] = item;
      });
      setExistingSpeciesInventory(inventory);
    } catch (error) {
      console.error('Error fetching species inventory:', error);
      setExistingSpeciesInventory({});
    } finally {
      setIsLoadingSpeciesInventory(false);
    }
  };

  const handleSpeciesChange = (selectedSpecies: string[]) => {
    const normalized = Array.from(new Set(selectedSpecies.map(s => s.toLowerCase())));
    setFormData(prev => ({
      ...prev,
      fish_species: normalized
    }));
  };

  const cycleNumberOptions = [
    { value: 1, label: '1-Cycle (Feb-Dec, target 1kg)' },
    { value: 2, label: '2-Cycle (Jul+Nov harvests, 650g each)' },
    { value: 3, label: '3-Cycle (Jun+Sep+Dec harvests, 600g each)' }
  ];

  // Fetch ponds when component opens
  useEffect(() => {
    if (isOpen && selectedLocation) {
      fetchPonds();
    }
  }, [isOpen, selectedLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPonds = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await apiClient.get(`/locations/${selectedLocation.id}/ponds`);
      setPonds(response.data);
    } catch (err) {
      console.error('Error fetching ponds:', err);
    }
  };

  // Compute live totals (count and weighted avg weight) from species inventory for selected species
  const liveTotals = useMemo(() => {
    let totalCount = 0;
    let weightedSum = 0;
    let lastUpdated: string | null = null;

    formData.fish_species.forEach((species) => {
      const key = species.toLowerCase();
      const existing = existingSpeciesInventory[key];
      if (existing && existing.count > 0) {
        const count = Number(existing.count) || 0;
        const avgG = Number(existing.avg_weight_g) || 0;
        totalCount += count;
        weightedSum += count * avgG;
        if (existing.last_updated) {
          if (!lastUpdated || existing.last_updated > lastUpdated) {
            lastUpdated = existing.last_updated;
          }
        }
      }
    });

    const weightedAvgG = totalCount > 0 ? weightedSum / totalCount : 0;
    return { totalCount, weightedAvgG, lastUpdated };
  }, [existingSpeciesInventory, formData.fish_species]);

  // Auto-fill snapshot fields when manual override is off
  useEffect(() => {
    if (!manualOverride) {
      setFormData((prev) => ({
        ...prev,
        initial_stocking_count: liveTotals.totalCount || 0,
        initial_seed_size_g: liveTotals.weightedAvgG > 0 ? Number(liveTotals.weightedAvgG.toFixed(1)) : prev.initial_seed_size_g,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTotals.totalCount, liveTotals.weightedAvgG, manualOverride]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;

    setLoading(true);
    setError(null);

    try {
      // Determine effective snapshot values
      const effectiveCount = manualOverride ? formData.initial_stocking_count : liveTotals.totalCount;
      const effectiveAvgG = manualOverride ? formData.initial_seed_size_g : (liveTotals.weightedAvgG > 0 ? Number(liveTotals.weightedAvgG.toFixed(1)) : 100);

      const cycleData = {
        ...formData,
        initial_stocking_count: effectiveCount,
        initial_seed_size_g: effectiveAvgG,
        location_id: selectedLocation.id,
        planned_start_date: formData.planned_start_date,
        target_biomass_kg: (effectiveCount * formData.target_harvest_size_g) / 1000,
        // Ensure fish_species is sent as array for multi-species support
        fish_species: formData.fish_species.length === 1 ? formData.fish_species[0] : formData.fish_species
      };

      await apiClient.post('/cycles', cycleData);
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        pond_id: '',
        cycle_name: '',
        planned_start_date: '',
        fish_species: ['tilapia'],
        initial_seed_size_g: 100,
        target_harvest_size_g: 1000,
        initial_stocking_count: 5000,
        cycle_number: 1,
        season_year: new Date().getFullYear()
      });
      setManualOverride(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create cycle');
    } finally {
      setLoading(false);
    }
  };

  const generateCycleName = () => {
    const year = formData.season_year;
    const cycleType = formData.cycle_number === 1 ? 'Grower' : 'Multi';
    const cycleNum = formData.cycle_number;
    return `${year}-${cycleType}-Cycle${cycleNum}`;
  };

  const updateCycleName = () => {
    setFormData(prev => ({
      ...prev,
      cycle_name: generateCycleName()
    }));
  };

  // Auto-generate cycle name when key fields change
  useEffect(() => {
    updateCycleName();
  }, [formData.season_year, formData.cycle_number]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CalendarIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Cycle</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pond *
                </label>
                <select
                  name="pond_id"
                  value={formData.pond_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a pond</option>
                  {ponds.map(pond => (
                    <option key={pond.id} value={pond.id}>
                      {pond.name} ({pond.pond_type})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cycle Name *
                </label>
                <input
                  type="text"
                  name="cycle_name"
                  value={formData.cycle_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 2025-Grower-Cycle1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="planned_start_date"
                  value={formData.planned_start_date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Season Year *
                </label>
                <select
                  name="season_year"
                  value={formData.season_year}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={2024}>2024</option>
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                </select>
              </div>
            </div>
          </div>

          {/* Fish Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Fish Details</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fish Species *
                </label>
                <div className="space-y-2">
                  {speciesData ? speciesData.map(species => (
                    <label key={species.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.fish_species.includes(species.name.toLowerCase())}
                        onChange={(e) => {
                          const key = species.name.toLowerCase();
                          if (e.target.checked) {
                            if (!formData.fish_species.includes(key)) {
                              handleSpeciesChange([...formData.fish_species, key]);
                            }
                          } else {
                            const newSpecies = formData.fish_species.filter(s => s !== key);
                            if (newSpecies.length > 0) {
                              handleSpeciesChange(newSpecies);
                            }
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{species.name}</span>
                    </label>
                  )) : (
                    <>
                      {['tilapia', 'rohu', 'catla', 'mrigal', 'pangash', 'grass_carp'].map(species => (
                        <label key={species} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.fish_species.includes(species)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                if (!formData.fish_species.includes(species)) {
                                  handleSpeciesChange([...formData.fish_species, species]);
                                }
                              } else {
                                const newSpecies = formData.fish_species.filter(s => s !== species);
                                if (newSpecies.length > 0) {
                                  handleSpeciesChange(newSpecies);
                                }
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">{species.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select one or more species for mixed culture
                </p>
              </div>

              {/* Existing Species Inventory */}
              {formData.pond_id && formData.fish_species.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Existing Fish in Pond
                  </label>
                  
                  {isLoadingSpeciesInventory ? (
                    <div className="flex items-center justify-center p-4 bg-gray-50 rounded border">
                      <div className="text-sm text-gray-500">Loading species inventory...</div>
                    </div>
                  ) : Object.keys(existingSpeciesInventory).length > 0 ? (
                    <div className="space-y-2">
                    {formData.fish_species.map(species => {
                      const key = species.toLowerCase();
                      const existing = existingSpeciesInventory[key];
                        return (
                          <div key={species} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <span className="text-sm font-medium text-gray-700 capitalize">
                              {species.replace('_', ' ')}:
                            </span>
                            <div className="text-sm text-gray-600">
                              {existing ? (
                                <>
                                  <span className="font-medium">{existing.count.toLocaleString()}</span> fish
                                  {existing.avg_weight_g > 0 && (
                                    <span className="ml-2">({existing.avg_weight_g.toFixed(1)}g avg)</span>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-400">No existing fish</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {/* Snapshot vs Live Totals */}
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <div className="font-medium text-gray-800">Snapshot Totals</div>
                            <div className="text-gray-600">
                              Count: <span className="font-medium">{formData.initial_stocking_count.toLocaleString()}</span>
                              <span className="mx-2">•</span>
                              Avg Wt: <span className="font-medium">{Number(formData.initial_seed_size_g).toFixed(1)}g</span>
                              <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 align-middle">Snapshot</span>
                            </div>
                          </div>
                          <div className="text-sm text-right">
                            <div className="font-medium text-gray-800">Live Totals</div>
                            <div className="text-gray-600">
                              Count: <span className="font-medium">{liveTotals.totalCount.toLocaleString()}</span>
                              <span className="mx-2">•</span>
                              Avg Wt: <span className="font-medium">{liveTotals.weightedAvgG > 0 ? liveTotals.weightedAvgG.toFixed(1) : '—'}g</span>
                              <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 align-middle">Live</span>
                            </div>
                            {liveTotals.lastUpdated && (
                              <div className="text-xs text-gray-400">as of {new Date(liveTotals.lastUpdated).toLocaleString()}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-4 bg-gray-50 rounded border">
                      <div className="text-sm text-gray-500">No species inventory data available for this pond</div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1">
                    This shows current fish count for selected species in the pond
                  </p>
                </div>
              )}
            </div>

            {/* Manual override toggle */}
            <div className="flex items-center justify-between bg-gray-50 border rounded p-3">
              <div className="text-sm text-gray-700">Initial fields default to auto from live inventory. Enable to edit snapshot values.</div>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-700">Manual override</span>
                <input
                  type="checkbox"
                  checked={manualOverride}
                  onChange={(e) => setManualOverride(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Seed Size (g) *
                </label>
                <input
                  type="number"
                  name="initial_seed_size_g"
                  value={formData.initial_seed_size_g}
                  onChange={handleInputChange}
                  required
                  min="1"
                  step="0.1"
                  disabled={!manualOverride}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!manualOverride ? 'bg-gray-50 border-gray-200 text-gray-700' : 'border-gray-300'}`}
                />
                <div className="flex items-center gap-2 mt-1">
                  {!manualOverride && (
                    <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">auto</span>
                  )}
                  <p className="text-xs text-gray-500">e.g., 100g for grower, fry weight for nursery</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Harvest Size (g) *
                </label>
                <input
                  type="number"
                  name="target_harvest_size_g"
                  value={formData.target_harvest_size_g}
                  onChange={handleInputChange}
                  required
                  min="1"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., 1000g for 1-cycle, 600g for multi-cycle</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Stocking Count *
                </label>
                <input
                  type="number"
                  name="initial_stocking_count"
                  value={formData.initial_stocking_count}
                  onChange={handleInputChange}
                  required
                  min="1"
                  disabled={!manualOverride}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!manualOverride ? 'bg-gray-50 border-gray-200 text-gray-700' : 'border-gray-300'}`}
                />
                <div className="flex items-center gap-2 mt-1">
                  {!manualOverride && (
                    <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">auto</span>
                  )}
                  <p className="text-xs text-gray-500">Number of fish to stock</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cycle Strategy */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Cycle Strategy</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cycle Number *
              </label>
              <select
                name="cycle_number"
                value={formData.cycle_number}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {cycleNumberOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose the number of harvest cycles for this season
              </p>
            </div>
          </div>

          {/* Projected Revenue */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Projected Revenue</h4>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(projectedRevenue)}
            </div>
            <p className="text-xs text-blue-700 mt-1">
              Based on {formData.initial_stocking_count.toLocaleString()} fish × {formData.target_harvest_size_g}g × Species-specific pricing
            </p>
            
            {/* Species Revenue Breakdown */}
            {formData.fish_species.length > 1 && Object.keys(speciesRevenueBreakdown).length > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="text-xs font-medium text-blue-800 mb-2">Revenue by Species:</div>
                <div className="space-y-1">
                  {Object.entries(speciesRevenueBreakdown).map(([species, revenue]) => (
                    <div key={species} className="flex justify-between text-xs">
                      <span className="text-blue-700 capitalize">{species.replace('_', ' ')}:</span>
                      <span className="font-medium text-blue-800">{formatCurrency(revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Cycle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCycleForm;
